import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SortOrder, listFiltersService, ListTableSort } from "sprint-common-web";

import { CloudAssetOrigin } from "../../../../common/types/AssetOrigin";
import { AssetStatus } from "../../../../common/types/AssetStatus";
import { AssetType } from "../../../../common/types/AssetType";
import { RecordType } from "../../../../common/types/RecordType";

export interface AncillaryAssetsListUrlFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    statuses?: number[];
    types?: number[];
    recordTypes?: number[];
    origins?: number[];
    createdDateFrom?: Date;
    createdDateTo?: Date;
    updatedDateFrom?: Date;
    updatedDateTo?: Date;
}

const defaultFilters: AncillaryAssetsListUrlFilters = {
    page: 1,
    pageSize: 50,
    sortBy: "updatedAudit",
    sortOrder: "desc",
    statuses: [AssetStatus.Active],
    types: [AssetType.Cloud, AssetType.OnPrem],
    recordTypes: [RecordType.Video, RecordType.Audio, RecordType.Subtitle],
    origins: [CloudAssetOrigin.User, CloudAssetOrigin.System]
};

export const useAncillaryAssetsListUrlFilters = () => {
    const filtersService = useMemo(() => listFiltersService<AncillaryAssetsListUrlFilters>(defaultFilters), []);
    const location = useLocation();
    const history = useHistory();

    const [filters, setFilters] = useState<AncillaryAssetsListUrlFilters>(() =>
        filtersService.getSavedFilters(location.search)
    );

    useEffect(() => {
        filtersService.saveFilters(location.pathname, history, filters);
    }, [filters]);

    const setSort = ({ by: sortBy, order: sortOrder }: ListTableSort) => {
        setFilters((prevFilters: AncillaryAssetsListUrlFilters) => ({ ...prevFilters, sortBy, sortOrder, page: 1 }));
    };

    const setPage = (page: number) => {
        setFilters((prevFilters: AncillaryAssetsListUrlFilters) => ({ ...prevFilters, page }));
    };

    const setPageSize = (pageSize: number): void => {
        setFilters((prevFilters: AncillaryAssetsListUrlFilters) => ({ ...prevFilters, pageSize, page: 1 }));
    };

    const setStatusIds = (statusIds: number[] | undefined): void => {
        setFilters((prevFilters: AncillaryAssetsListUrlFilters) => ({ ...prevFilters, statuses: statusIds, page: 1 }));
    };

    const clearFilters = (): void => {
        setFilters(defaultFilters);
    };

    const setAssetTypes = (types?: number[]): void => {
        setFilters((prevFilters: AncillaryAssetsListUrlFilters) => ({ ...prevFilters, types, page: 1 }));
    };

    const setAssetRecordTypes = (recordTypes?: number[]): void => {
        setFilters((prevFilters: AncillaryAssetsListUrlFilters) => ({ ...prevFilters, recordTypes, page: 1 }));
    };

    const setAssetOrigins = (origins?: number[]): void => {
        setFilters((prevFilters: AncillaryAssetsListUrlFilters) => ({ ...prevFilters, origins, page: 1 }));
    };

    const setCreatedDateFrom = (date?: Date): void => {
        setFilters((prevFilters: AncillaryAssetsListUrlFilters) => ({
            ...prevFilters,
            createdDateFrom: date,
            page: 1
        }));
    };

    const setCreatedDateTo = (date?: Date): void => {
        setFilters((prevFilters: AncillaryAssetsListUrlFilters) => ({ ...prevFilters, createdDateTo: date, page: 1 }));
    };

    const setUpdatedDateFrom = (date?: Date): void => {
        setFilters((prevFilters: AncillaryAssetsListUrlFilters) => ({
            ...prevFilters,
            updatedDateFrom: date,
            page: 1
        }));
    };

    const setUpdatedDateTo = (date?: Date): void => {
        setFilters((prevFilters: AncillaryAssetsListUrlFilters) => ({ ...prevFilters, updatedDateTo: date, page: 1 }));
    };

    return {
        filters,
        setStatusIds,
        setSort,
        setPage,
        setPageSize,
        clearFilters,
        setAssetTypes,
        setAssetRecordTypes,
        setAssetOrigins,
        setCreatedDateFrom,
        setCreatedDateTo,
        setUpdatedDateFrom,
        setUpdatedDateTo
    };
};
